import React, { useEffect, useRef, useState } from "react";
import { api, SERVICES } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";

const YES_NO = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const STEPS = [
  { key: "service", type: "choice", bot: "Hey! Let's get you a quote in about a minute. What do you need hauled?", options: SERVICES.map((s) => ({ label: s.title, value: s.slug })) },
  { key: "item_location", type: "choice", bot: "Where are the items right now?", options: [
    { label: "Inside the home / building", value: "inside" },
    { label: "Outside (yard, garage, driveway)", value: "outside" },
    { label: "Already at curbside", value: "curbside" },
  ]},
  { key: "stairs_or_elevator", type: "choice", bot: "Any stairs or elevator involved?", options: YES_NO },
  { key: "heavy_items", type: "choice", bot: "Any heavy items? (safe, piano, hot tub, etc.)", options: YES_NO },
  { key: "construction_debris", type: "choice", bot: "Is this construction or remodel debris?", options: YES_NO },
  { key: "same_day_needed", type: "choice", bot: "Do you need same-day pickup?", options: YES_NO },
  { key: "description", type: "text", bot: "Got it. Tell me a bit about the job — what's being hauled, quantity, size, and any access notes.", placeholder: "e.g. Old couch and fridge in the garage, easy side-door access.", min: 10 },
  { key: "name", type: "text", bot: "Perfect. Who am I speaking with? (Full name)", min: 2, placeholder: "Full name" },
  { key: "phone", type: "text", bot: "Thanks! What's the best phone number to reach you?", min: 7, placeholder: "(813) 555-0100" },
  { key: "email", type: "email", bot: "And your email? We'll send the written quote there.", placeholder: "you@example.com" },
  { key: "address", type: "text", bot: "What's the full pickup address? Street, city, state.", min: 8, placeholder: "1234 Oak Ln, Wesley Chapel, FL" },
  { key: "zip_code", type: "text", bot: "ZIP code?", min: 3, placeholder: "33544" },
  { key: "consent_contact", type: "choice", bot: "Last thing — do you agree to be contacted by phone, text, or email about this request? (Required)", options: YES_NO },
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const zipRe = /^\d{3,}$/;

const ChatQuote = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("(813) 555-0100");
  const [stepIdx, setStepIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setPhone(data.business_phone)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: "bot", text: STEPS[0].bot }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, submitting]);

  const current = STEPS[stepIdx];

  const advance = (value, label) => {
    // Special handling: consent must be true
    if (current.key === "consent_contact" && value !== true) {
      toast.error("We need your consent to reach out with the quote.");
      setMessages((m) => [...m, { from: "user", text: label ?? String(value) }, { from: "bot", text: "No worries — we can't send a quote without permission to contact you. Tap Yes to continue." }]);
      return;
    }
    const newAnswers = { ...answers, [current.key]: value };
    setAnswers(newAnswers);
    const nextIdx = stepIdx + 1;
    setMessages((m) => [...m, { from: "user", text: label ?? String(value) }]);
    if (nextIdx < STEPS.length) {
      setTimeout(() => {
        setMessages((m) => [...m, { from: "bot", text: STEPS[nextIdx].bot }]);
        setStepIdx(nextIdx);
      }, 400);
    } else {
      finalSubmit(newAnswers);
    }
    setInput("");
  };

  const finalSubmit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      await api.post("/leads", { ...finalAnswers, source: "chat" });
      setDone(true);
      setMessages((m) => [
        ...m,
        { from: "bot", text: "Awesome — got it! We'll call you back shortly with a firm price. If it's urgent, feel free to call us directly." },
      ]);
      toast.success("Quote request received!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : "Something went wrong. Please try again or call us.";
      setMessages((m) => [...m, { from: "bot", text: `Hmm — ${msg}` }]);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    const v = input.trim();
    if (!v) return;
    if (current.min && v.length < current.min) {
      toast.error(`Please give a bit more detail (at least ${current.min} characters).`);
      return;
    }
    if (current.type === "email" && !emailRe.test(v)) {
      toast.error("That doesn't look like a valid email.");
      return;
    }
    if (current.key === "zip_code" && !zipRe.test(v)) {
      toast.error("ZIP code should be digits.");
      return;
    }
    advance(v);
  };

  const reset = () => {
    setStepIdx(0);
    setMessages([{ from: "bot", text: STEPS[0].bot }]);
    setAnswers({});
    setInput("");
    setDone(false);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 z-50 hidden items-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-[hsl(16,55%,42%)] md:flex"
          data-testid="chat-open-btn"
          aria-label="Open quote chat"
        >
          <MessageCircle className="h-5 w-5" /> Chat for a Quote
        </button>
      )}

      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[560px] max-h-[calc(100vh-8rem)] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-sm border border-border bg-card shadow-2xl" data-testid="chat-widget">
          <div className="flex items-center justify-between border-b border-border bg-[hsl(60,6%,11%)] p-4 text-white">
            <div>
              <p className="font-display text-sm font-black uppercase tracking-wider">Fast Junk Pros</p>
              <p className="text-xs text-white/70">Free quote in about a minute</p>
            </div>
            <button onClick={() => setOpen(false)} className="rounded-sm p-1 hover:bg-white/10" data-testid="chat-close-btn" aria-label="Close chat">
              <X className="h-5 w-5" />
            </button>
          </div>

          <a
            href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
            className="flex items-center justify-center gap-2 border-b border-border bg-[hsl(145,27%,24%)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[hsl(145,27%,20%)]"
            data-testid="chat-call-link"
          >
            <Phone className="h-4 w-4" /> Prefer to talk? Call {phone}
          </a>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-muted p-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-sm px-3 py-2 text-sm ${
                    m.from === "user"
                      ? "bg-primary text-primary-foreground"
                      : "border border-border bg-card"
                  }`}
                  data-testid={`chat-msg-${m.from}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {submitting && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Sending your request…
                </div>
              </div>
            )}
            {done && (
              <div className="flex justify-center pt-2">
                <div className="flex items-center gap-2 rounded-sm bg-[hsl(145,27%,90%)] px-3 py-2 text-xs font-semibold text-[hsl(145,27%,24%)]">
                  <CheckCircle2 className="h-4 w-4" /> Request received
                </div>
              </div>
            )}
          </div>

          {!done && current.type === "choice" && (
            <div className="border-t border-border bg-card p-3">
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {current.options.map((o) => (
                  <button
                    key={String(o.value)}
                    onClick={() => advance(o.value, o.label)}
                    className="rounded-sm border border-border bg-white px-3 py-2 text-left text-xs font-medium hover:border-primary hover:text-primary"
                    data-testid={`chat-choice-${String(o.value)}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!done && current.type !== "choice" && (
            <form onSubmit={handleTextSubmit} className="border-t border-border bg-card p-3">
              <div className="flex gap-2">
                <input
                  type={current.type === "email" ? "email" : "text"}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={current.placeholder}
                  className="flex-1 rounded-sm border border-input bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
                  data-testid="chat-text-input"
                  autoFocus
                />
                <button type="submit" className="rounded-sm bg-primary px-3 py-2 text-primary-foreground hover:bg-[hsl(16,55%,42%)]" data-testid="chat-send-btn" aria-label="Send">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}

          {done && (
            <div className="border-t border-border bg-card p-3">
              <button onClick={reset} className="btn-cta w-full" data-testid="chat-reset-btn">
                Start another request
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatQuote;
